/** Mirrors `api/src/journey/` on the Rust side. */

/**
 * A journey document, as the geo API stores it.
 *
 * Lenient on purpose, exactly as the store is: this is only ever used to *draw*
 * a journey and to say roughly how long it is. Whether it can actually be
 * played is the API's decision, and it says so in words when it refuses.
 */
export interface JourneyWaypoint {
  lat: number
  lng: number
  level: number
  floor_id?: string | null
  label?: string | null
  dwell_s?: number | null
  speed_mps?: number | null
  transition_s?: number | null
}

export interface Journey {
  id: string
  organization_id?: string | null
  format?: string | null
  name: string
  place_id?: string | null
  speed_mps?: number | null
  waypoints: JourneyWaypoint[]
  createdAt?: string
  updatedAt?: string
}

export type RunState = 'running' | 'paused' | 'finished' | 'stopped' | 'failed'
export type RunSink = 'preview' | 'relay'

/** One leg of the precomputed timeline, straight off the run. */
export interface RunLeg {
  kind: 'dwell' | 'walk'
  from: number
  to?: number
  distance_m: number
  duration_s: number
  level_change: boolean
  start_s: number
  level: number
}

/**
 * A run, as the API reports it.
 *
 * `started_at` is when the run last entered `running` — not when it was created
 * — and `paused_elapsed_s` is the journey time already consumed before that
 * moment. With `speed` and `legs` that is everything needed to interpolate
 * progress locally, which is why there is no per-second message.
 */
export interface Run {
  id: string
  journey_id: string
  name: string
  state: RunState
  started_at: string
  paused_elapsed_s: number
  duration_s: number
  distance_m: number
  speed: number
  rate_hz: number
  ground_floor_no: number
  loop: boolean
  tag_id: string | null
  /**
   * Which of the organisation's walkers on the relay is playing it, 1..N.
   * Null for a preview, which holds no wristband. `tag_id` is that walker's.
   */
  walker: number | null
  sink: RunSink
  /** Which configured relay this run is played into. Null for a preview. */
  relay: string | null
  legs: RunLeg[]
  error?: string
}

export interface RunsResponse {
  runs: Run[]
}

/** What `POST /v1/runs` takes. */
export interface StartRunRequest {
  journey_id: string
  sink: RunSink
  /** A configured relay's name. Omitted means the server's default one. */
  relay?: string
  /**
   * Which walker slot to play as, 1..N. Omitted means the lowest slot this
   * organisation is not already playing on that relay. Never a tag id — the
   * mapping from slot to wristband number is the server's.
   */
  walker?: number
  ground_floor_no?: number
  rate_hz?: number
  speed?: number
  loop?: boolean
}

export const isLive = (run: Run): boolean => run.state === 'running' || run.state === 'paused'
